<template>
	<div v-show="isVisible" class="flex bg-lo-warning-subtle" :class="[{ 'p-4 rounded-md ': !isCompact }, { 'py-2 px-4 text-md': isCompact }]">
		<div class="flex flex-col flex-grow">
			<span v-if="!isCompact" class="font-bold text-lg" v-html="maintenanceTitle"/>
			<span :class="[{ 'mt-1': !isCompact }]" v-html="maintenanceMessage"/>
		</div>
		<div class="flex h-full" :class="[{ 'w-6 pt-1.5': !isCompact }]">
			<img v-if="showDismissButton" src="@/assets/images/icon/dismiss.svg" alt="Dismiss" title="Dismiss" class="size-4 my-auto cursor-pointer" @click="dismiss">
		</div>
	</div>

	<MultiButtonModal
		v-if="showJwtDialog"
		:show="true"
		icon="alert"
		title="Reset JWT Version Code"
		message="Maintenance has begun. Do you want to reset the JWT version code and kick everyone but you out?"
		:buttons="[
			new ModalButtonData(
				'No', () => { showJwtDialog = false; getStatus() }, ButtonType.Default, ButtonSize.Small
			),
			new ModalButtonData(
				'Reset', () => { resetJwt() }, ButtonType.Default, ButtonSize.Small, isResettingJwt
			)
		]"
	/>
</template>

<script setup lang="js">

import { onBeforeMount, ref, watch } from 'vue'
import { maintenanceApi } from '@/api/Support/MaintenanceApi.js'
import { Util as Utils } from '@/models/utils/Util.js'
import { Format } from '@/models/utils/FormatUtils.js'
import { useAuthStore } from '@/stores/authStore.js'
import { useBroadcastChannel } from '@vueuse/core'
import { ModalButtonData } from '@/components/Alerts/ModalButtonData.js'
import { ButtonSize, ButtonType } from '@/components/Forms/Buttons/SubmitButtonConstants.js'
import MultiButtonModal from '@/components/Alerts/MultiButtonModal.vue'
import { useToast } from 'vue-toastification'

const maintenanceTitle = ref(null)
const maintenanceMessage = ref(null)
const isVisible = ref(false)
const countdownInterval = ref(null)
const showDismissButton = ref(false)
const showJwtDialog = ref(false)
const isResettingJwt = ref(false)
const toaster = useToast()

const props = defineProps({
	compact: { type: Boolean, default: false },
	showIfStatus: { type: [ String, Array ], default: null }
})

const isCompact = ref(props.compact)
const channel = useBroadcastChannel({ name: 'MaintenancePanel' })

onBeforeMount(() => {
	getStatus()

	watch(
		channel.data,
		(data) => {
			if (data.promptToReset) {
				showJwtDialog.value = true
			}
			else {
				getStatus()
			}
		})
})

function getStatus() {
	maintenanceApi.getMaintenanceStatus((response) => {
		if (response && response.status) {
			const status = response.status
			switch (status.status) {
				case 'pending': {
					const startAtTime = Format.date(status.expected_start, {
						hour: '2-digit',
						minute: '2-digit',
						timeZoneName: 'short'
					})

					const startAtDate = Format.date(status.expected_start, {
						month: '2-digit',
						day: '2-digit',
					})

					const endAtTime = Format.date(status.expected_end, {
						hour: '2-digit',
						minute: '2-digit',
						timeZoneName: 'short'
					})

					const endAtDate = Format.date(status.expected_end, {
						month: '2-digit',
						day: '2-digit',
					})

					const minutes = ((new Date(status.expected_end)).getTime() - (new Date(status.expected_start)).getTime()) / 1000 / 60
					const duration = Format.durationFromMinutes(minutes)

					maintenanceTitle.value = "Upcoming Scheduled Maintenance"
					maintenanceMessage.value = Utils.formatStringWithKvp(status.pending_message, {
						expected_duration: duration,
						expected_start_time: startAtTime,
						expected_start_date: startAtDate,
						expected_end_time: endAtTime,
						expected_end_date: endAtDate
					})

					showDismissButton.value = true
					setVisibility(status.status)
					break
				}
				case 'warning': {
					setWarningMessage(status)
					setVisibility(status.status)
					break
				}
				case 'in_progress': {
					const endAtTime = Format.date(status.expected_end, {
						hour: '2-digit',
						minute: '2-digit',
						timeZoneName: 'short'
					})

					const endAtDate = Format.date(status.expected_end, {
						month: '2-digit',
						day: '2-digit',
					})

					maintenanceTitle.value = "Scheduled Maintenance"
					maintenanceMessage.value = Utils.formatStringWithKvp(status.in_progress_message, {
						expected_end_time: endAtTime,
						expected_end_date: endAtDate
					})

					setVisibility(status.status)
					break
				}
				default:
					isVisible.value = false
					break
			}
		}
		else {
			isVisible.value = false
		}
	}, (e) => {
		isVisible.value = false
	})
}

function setVisibility(status) {
	if (props.showIfStatus) {
		if (Array.isArray(props.showIfStatus)) {
			isVisible.value = props.showIfStatus.includes(status)
		}
		else {
			isVisible.value = props.showIfStatus === status
		}
	}
	else {
		isVisible.value = true
	}
}

function formatTime (millis) {
	const minutes = Math.floor(millis / 60000)
	const seconds = Math.floor((millis % 60000) / 1000)
	return `${minutes}:${seconds.toString().padStart(2, '0')}`
}

function dismiss() {
	clearInterval(countdownInterval.value)
	isVisible.value = false
}

function setWarningMessage(response) {
	const startedAtTime = new Date(response.started_at)
	const targetDateTime = new Date(startedAtTime.getTime() + response.warning_minutes * 60000)

	const now = new Date()

	const millisRemaining = (targetDateTime.getTime() - now.getTime())
	const timeRemaining = formatTime(millisRemaining)

	showDismissButton.value = millisRemaining > 30000 // Don't let them dismiss when 30 seconds remain
	maintenanceMessage.value = Utils.formatStringWithKvp(response.warning_message, {
		time_until_start: timeRemaining,
	})

	if (countdownInterval.value) {
		clearInterval(countdownInterval.value)
	}

	countdownInterval.value = setInterval(() => {
		const now = new Date()
		const millisRemaining = (targetDateTime.getTime() - now.getTime())

		if (millisRemaining <= 0) {
			clearInterval(countdownInterval.value)

			if (useAuthStore().isDeveloper) {
				showJwtDialog.value = true
			}
			else {
				useAuthStore().unAuthenticated = true
			}
		}
		else {
			const timeRemaining = formatTime(millisRemaining)

			showDismissButton.value = millisRemaining > 30000 // Don't let them dismiss when 30 seconds remain
			maintenanceMessage.value = Utils.formatStringWithKvp(response.warning_message, {
				time_until_start: timeRemaining,
			})
		}
	}, 1000)
}

function resetJwt() {
	isResettingJwt.value = true

	maintenanceApi.resetJwtVersionCode((response) => {
		useAuthStore().setJwtVersionCode(response.jwt_version_code)
		getStatus()

		showJwtDialog.value = false
		isResettingJwt.value = false

		toaster.success("JWT version code reset.")
	}, (e) => {
		showJwtDialog.value = false
		isResettingJwt.value = false
		getStatus()

		toaster.error(e.showErrorBanner())
	})
}
</script>